const normalizeOrgToken = (value: string): string => value.toLowerCase().replaceAll(/[^a-z0-9]/g, '');

// Maps a normalized display-name token to its canonical lookup-table abbreviation.
// "Field Ops" is rendered to users as "fieldops" after normalization, but the
// approved-orgs lookup table records the org with the short code "fo".
const ORG_ALIASES: Record<string, string> = {
  fieldops: 'fo',
};

const toCanonicalOrgCode = (value: string): string => {
  const normalized = normalizeOrgToken(value);
  return ORG_ALIASES[normalized] ?? normalized;
};

// Reverse of ORG_ALIASES: maps an approving-org short code to the group name the
// user/authorization services store on internal users. The internal-user lookup
// matches on those group names, so a row approved by "FO" must be sent as "FieldOps".
const APPROVING_ORG_TO_USER_GROUP: Record<string, string> = {
  fo: 'FieldOps',
};

export const toUserGroupName = (approvingOrgName: string): string =>
  APPROVING_ORG_TO_USER_GROUP[normalizeOrgToken(approvingOrgName)] ??
  approvingOrgName;

export const isUserInApprovedOrg = (
  userOrganizationName: string | null | undefined,
  approvedOrgNames: readonly string[]
): boolean => {
  if (!userOrganizationName || approvedOrgNames.length === 0) {
    return false;
  }

  const userOrgCode = toCanonicalOrgCode(userOrganizationName);
  if (!userOrgCode) {
    return false;
  }

  const approvedOrgCodes = new Set(
    approvedOrgNames
      .map((orgName) => orgName?.trim())
      .filter((orgName): orgName is string => Boolean(orgName))
      .map(toCanonicalOrgCode)
  );

  return approvedOrgCodes.has(userOrgCode);
};
